import type { ProductType } from "@component/assets/AbstractLeadForm/types";
import type { AbstractListingCalculatorState } from "@component/assets/AbstractListingCalculator/AbstractListingCalculator";
import { AbstractListingCalculator } from "@component/assets/AbstractListingCalculator/AbstractListingCalculator";
import type { ResultListResponse } from "@component/assets/AbstractListingCalculator/types";
import { getBankName } from "@component/assets/data/banks";
import { LandingCover } from "@landing/components/LandingCover";
import autobind from "autobind-decorator";
import React from "react";
import type { ReactNode } from "react";
import { RetailAccountResultList } from "./RetailAccountResultList";
import { RetailAccountSearchForm } from "./RetailAccountSearchForm";
import {
	BASIC_SEARCH_FIELDS,
	INIT_VALUES,
	calculate,
	exportXls,
	responseToResult,
	searchParamsToRequest,
} from "./domain";
import type {
	RetailAccountRequest,
	RetailAccountResponseItem,
	RetailAccountSearchParams,
	RetailAccountSort,
} from "./types";
import "./style.scss";

type State = AbstractListingCalculatorState<
	RetailAccountSearchParams,
	RetailAccountRequest,
	RetailAccountResponseItem,
	RetailAccountSort
>;

export class RetailAccountCalculator extends AbstractListingCalculator<
	RetailAccountSearchParams,
	RetailAccountRequest,
	RetailAccountResponseItem,
	RetailAccountSort
> {
	// EventParams
	protected readonly productTypes: string = "RETAIL_ACCOUNT";
	protected readonly componentType: string = "listing-calculator";

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			sort: "FIRST_AND_SECOND_YEAR_TOTAL_FEE",
		};
	}

	protected getDefaultValues(): RetailAccountSearchParams {
		const { highlightedBank } = this.props;

		if (highlightedBank) {
			return { ...INIT_VALUES, onePerBank: false };
		}

		return INIT_VALUES;
	}

	protected renderContent(): ReactNode {
		const { highlightedBank, fakeHeader } = this.props;

		return (
			<>
				<LandingCover title={this.getTitle()} subTitle={this.getSubTitle()} fakeHeader={fakeHeader}>
					<RetailAccountSearchForm
						buttonText="Bankszámlák összehasonlítása"
						highlightedBank={highlightedBank}
						withOnePerBank={!highlightedBank}
						{...this.getSearchFormProps()}
					/>
				</LandingCover>
				<RetailAccountResultList {...this.getResultListProps()} />
			</>
		);
	}

	private getTitle(): ReactNode {
		const { highlightedBank } = this.props;

		if (highlightedBank) {
			return `${getBankName(highlightedBank)} - bankszámla kalkulátor`;
		}

		return "Bankszámla kereső";
	}

	private getSubTitle(): ReactNode {
		const { highlightedBank } = this.props;

		if (highlightedBank) {
			return (
				<>
					Tudd meg, hogy melyik {getBankName(highlightedBank)} bankszámla a megfelelő számodra, vagy{" "}
					<strong>hasonlítsd össze</strong> más bankok ajánlataival és nézd meg, találsz-e jobbat!
				</>
			);
		}

		return "Segítünk megtalálni a legjobb bankszámlát gyorsan és egyszerűen";
	}

	protected getRequestFromSearchParams(searchParams: RetailAccountSearchParams): RetailAccountRequest {
		return searchParamsToRequest(searchParams, this.state?.sort);
	}

	protected async getResult(request: RetailAccountRequest, searchParams: RetailAccountSearchParams): Promise<any> {
		return calculate(request, searchParams.onePerBank);
	}

	protected getResultList(
		_request: RetailAccountRequest,
		_searchParams: RetailAccountSearchParams,
		response: any,
	): ResultListResponse<RetailAccountResponseItem> {
		return responseToResult(response);
	}

	@autobind
	protected async onExport(searchParams: RetailAccountSearchParams): Promise<string> {
		const { sort } = this.state;

		return exportXls(searchParams, sort);
	}

	protected getClassName(): string {
		return `${super.getClassName()} retail-account`;
	}

	protected getProductType(): ProductType {
		return "RETAIL_ACCOUNT";
	}

	protected getBasicSearchFields(): string[] {
		const { highlightedBank } = this.props;

		return [...BASIC_SEARCH_FIELDS, !highlightedBank ? "onePerBank" : undefined];
	}

	protected isDetailed(searchParams: RetailAccountSearchParams): boolean {
		return super.isDetailed(searchParams, ["bank"]);
	}

	protected getComponentName(): string {
		return "RetailAccountCalculator";
	}
}
